declare const window: any

class ImageBrightnessDetector {
    static init() {
        window.ImageBrightnessDetector = this
    }

    /**
     * Calculates the average brightness level of an image using luminosity formula.
     * Used to determine if a logo is dark or light for appropriate styling.
     *
     * @param imageUrl - The URL of the image to analyze
     * @returns Promise resolving to brightness level (0-255, where 255 is brightest)
     *          Returns 255 (light) as default if image fails to load
     */
    static getBrightnessLevel = (imageUrl: string): Promise<number> => {
        const DEFAULT_BRIGHTNESS = 255

        return new Promise((resolve) => {
            const imageElement = new Image()
            imageElement.crossOrigin = 'Anonymous'

            imageElement.onload = () => {
                let totalPixelCount = 0

                const canvasElement = document.createElement('canvas')
                if (!canvasElement) {
                    console.warn(
                        '[ImageBrightnessDetector] Failed to create canvas element'
                    )
                    resolve(DEFAULT_BRIGHTNESS)

                    return
                }

                const canvasContext = canvasElement.getContext('2d')
                if (!canvasContext) {
                    console.warn(
                        '[ImageBrightnessDetector] Failed to get 2D canvas context'
                    )
                    resolve(DEFAULT_BRIGHTNESS)

                    return
                }

                canvasContext.drawImage(imageElement, 0, 0)
                // Extract pixel data
                const imageData = canvasContext.getImageData(
                    0,
                    0,
                    imageElement.width,
                    imageElement.height
                )
                const pixelDataArray = imageData.data

                // Dictionary to store color frequencies
                let colorFrequencyHistogram: any = {}
                let nonTransparentPixelCount = 0

                // Fill the histogram with color frequencies
                for (let ii = 0; ii < pixelDataArray.length; ii += 4) {
                    const redValue = pixelDataArray[ii]
                    const greenValue = pixelDataArray[ii + 1]
                    const blueValue = pixelDataArray[ii + 2]
                    // Transparency
                    const alphaValue = pixelDataArray[ii + 3]

                    // Skip transparent pixels
                    if (alphaValue === 0) {
                        continue
                    }

                    // Use RGBA as the key
                    const colorKey = `${redValue},${greenValue},${blueValue},${alphaValue}`

                    if (!(colorKey in colorFrequencyHistogram)) {
                        colorFrequencyHistogram[colorKey] = 0
                    }

                    colorFrequencyHistogram[colorKey] += 1
                    totalPixelCount++
                }

                // Calculate the average brightness using the histogram
                let totalLuminosity = 0

                for (const colorKey in colorFrequencyHistogram) {
                    const [redValue, greenValue, blueValue, alphaValue] =
                        colorKey.split(',').map(Number)
                    const colorFrequency = colorFrequencyHistogram[colorKey]

                    // Mix colors with transparency
                    const redMixed =
                        redValue * (alphaValue / 255)
                        + 255 * (1 - alphaValue / 255)
                    const greenMixed =
                        greenValue * (alphaValue / 255)
                        + 255 * (1 - alphaValue / 255)
                    const blueMixed =
                        blueValue * (alphaValue / 255)
                        + 255 * (1 - alphaValue / 255)

                    // Calculate brightness for the color
                    const colorBrightness =
                        0.299 * redMixed
                        + 0.587 * greenMixed
                        + 0.114 * blueMixed

                    totalLuminosity += colorBrightness * colorFrequency
                    nonTransparentPixelCount += colorFrequency
                }

                // Return average brightness level
                const averageBrightnessLevel = Number(
                    (totalLuminosity / nonTransparentPixelCount).toFixed(2)
                )

                resolve(averageBrightnessLevel)
            }

            imageElement.onerror = () => {
                console.warn(
                    `[ImageBrightnessDetector] Failed to load image: ${imageUrl}`
                )
                resolve(DEFAULT_BRIGHTNESS)
            }

            imageElement.src = imageUrl
        })
    }
}

export default ImageBrightnessDetector
